/**
 * useSeo — i18n-aware SEO head 管理
 *  - title / description / og / twitter / canonical / hreflang / json-ld
 *  - <html lang> 跟 locale 走
 *  - 走 @unhead/vue（vite-ssg 已内置支持）
 *
 * 关键：tm() 调用必须放在 useHead 的函数体内，locale 切换时函数重算 → title/meta 实时更新
 */
import { watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { SUPPORTED_LOCALES } from '@/config'

export type SeoPage =
  | 'home'
  | 'install'
  | 'guide'
  | 'about'
  | 'privacy'
  | 'terms'
  | 'refund'
  | 'inviteRules'
  | 'client'
  | 'notFound'
  | 'subscribe'
  | 'login'
  | 'account'

export interface UseSeoOptions {
  page?: SeoPage
  title?: string
  description?: string
  ogImage?: string
  ogType?: 'website' | 'article'
  /**
   * robots meta 指令。默认 'index, follow'。
   * 过渡页（如 /client）传 'noindex, follow'：不进入搜索索引但保留外链权益。
   */
  robots?: 'index, follow' | 'noindex, follow' | 'noindex, nofollow'
  /** 结构化数据；可传 getter（locale 变化 / 数据到达后随 patch 重建） */
  jsonLd?:
    | Record<string, unknown>
    | Record<string, unknown>[]
    | (() => Record<string, unknown> | Record<string, unknown>[] | null)
}

const OG_LOCALES: Record<string, string> = {
  'zh-CN': 'zh_CN',
  'zh-TW': 'zh_TW',
  en: 'en_US'
}

const SITE_URL = 'https://woofootball.site'

/**
 * 调用一次以让 <html lang> 跟当前 i18n locale 走
 * 建议在 App.vue 顶层调用
 */
export function useHtmlLang() {
  const { locale } = useI18n()
  watchEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = String(locale.value)
    }
  })
}

function resolvePage(options: UseSeoOptions, routeName: string | null | undefined): SeoPage {
  if (options.page) return options.page
  const name = String(routeName ?? 'home')
  if (
    name === 'home' ||
    name === 'install' ||
    name === 'guide' ||
    name === 'about' ||
    name === 'privacy' ||
    name === 'terms' ||
    name === 'refund' ||
    name === 'inviteRules' ||
    name === 'client' ||
    name === 'subscribe' ||
    name === 'login' ||
    name === 'account' ||
    name === 'notFound'
  ) {
    return name as SeoPage
  }
  return 'home'
}

/**
 * 设置当前页面的 SEO meta。
 *   useSeo()              → 自动从 route.name 推断 page
 *   useSeo('install')     → 显式指定
 *   useSeo({ page: 'home', title: '自定义', jsonLd: {...} })
 */
export function useSeo(arg?: SeoPage | UseSeoOptions) {
  const { t, locale } = useI18n()
  const route = useRoute()

  const options: UseSeoOptions = typeof arg === 'string' ? { page: arg } : (arg ?? {})

  // 路径 + 显式 options 在 setup 时一次性求值（route.path 变化时 Vue 路由会重挂载组件 → useSeo 重新调用）
  const page = resolvePage(options, typeof route.name === 'string' ? route.name : String(route.name ?? ''))
  const ogType = options.ogType ?? 'website'
  const explicitTitle = options.title
  const explicitDescription = options.description
  const explicitOgImage = options.ogImage
  const explicitRobots = options.robots ?? 'index, follow'
  function resolveJsonLd(): Array<{ type: 'application/ld+json'; innerHTML: string }> {
    const input = options.jsonLd
    if (!input) return []
    const value = typeof input === 'function' ? input() : input
    if (!value) return []
    return (Array.isArray(value) ? value : [value]).map((data) => ({
      type: 'application/ld+json' as const,
      innerHTML: JSON.stringify(data)
    }))
  }

  function build(): Parameters<typeof useHead>[0] {
    const siteName = String(t('seo.siteName'))
    const description = explicitDescription ?? String(t(`seo.${page}.description`))
    const keywords = String(t('seo.keywords'))
    const ogImagePath = explicitOgImage ?? String(t('seo.ogImage'))
    const ogImage = ogImagePath.startsWith('http') ? ogImagePath : `${SITE_URL}${ogImagePath}`
    const title = explicitTitle ?? String(t(`seo.${page}.title`))
    const canonical = `${SITE_URL}${route.path}`
    const ogLocale = OG_LOCALES[String(locale.value)] ?? 'en_US'

    const hreflangs: Array<{ rel: string; hreflang: string; href: string }> = SUPPORTED_LOCALES.map((l) => ({
      rel: 'alternate',
      hreflang: l,
      href: `${SITE_URL}${route.path}${l === 'zh-CN' ? '' : `?lang=${l}`}`
    }))
    hreflangs.push({
      rel: 'alternate',
      hreflang: 'x-default',
      href: `${SITE_URL}${route.path}`
    })

    return {
      title,
      htmlAttrs: { lang: String(locale.value) },
      meta: [
        { name: 'description', content: description },
        { name: 'keywords', content: keywords },
        { name: 'author', content: siteName },
        { name: 'robots', content: explicitRobots },

        { property: 'og:type', content: ogType },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:image', content: ogImage },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:alt', content: title },
        { property: 'og:url', content: canonical },
        { property: 'og:site_name', content: siteName },
        { property: 'og:locale', content: ogLocale },

        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: ogImage }
      ],
      link: [{ rel: 'canonical', href: canonical }, ...hreflangs],
      script: resolveJsonLd()
    }
  }

  // SSR：build() 立即求值，useHead 注册静态 head entry
  // 客户端：拿 entry 引用，watch locale/route 变化时直接 entry.patch() 更新
  const entry = useHead(build()) as unknown as { patch?: (input: unknown) => void } | undefined

  // 显式 watch：locale 切换 / route 切换时重新 build 并 patch
  watch(
    [() => locale.value, () => route.path],
    () => {
      if (entry && typeof entry.patch === 'function') {
        try {
          entry.patch(build())
        } catch {
          // 忽略
        }
      } else if (typeof document !== 'undefined') {
        // 兜底：直接改 document.title（SSR / 异常时）
        document.title = String(t(`seo.${page}.title`))
      }
    }
  )
}
